import React, { useCallback, useState } from "react";

import { Button, Stack } from "@mui/material";
import { DealBarCharacteristic } from "store/types";

import { PopupEmptyState } from "../PopupEmptyState";
import { MultipleData } from "./MultipleData";
import { SingleData } from "./SingleData";

interface Props {
  characteristic: DealBarCharacteristic;
  deal_id: number;
  onSubmit: (data: DataItem[]) => Promise<void> | void;
  onCancel: () => void;
  viewOnly?: boolean;
}

export type DataItem = { is_deleted: boolean; id: number | string } & Omit<DealBarCharacteristic["data"][0], "id">;

export type UpdateItem = (params: Partial<DataItem>) => void;
const decimalPercentage = 2;

export const SingleCharForm: React.FC<Props> = ({ characteristic, deal_id, onSubmit, onCancel, viewOnly }) => {
  const { id, data, name, data_type, is_multiple } = characteristic;

  const [state, setState] = useState<DataItem[]>(data.map((item) => ({ ...item, is_deleted: false })));

  const buildNewCharData = useCallback(
    (params?: Partial<Pick<DataItem, "value" | "percentage">>): DataItem => {
      // Find max id of the characteristics data
      const maxId = state.reduce((acc, item) => {
        const numberId = Number(item.id);
        return numberId > acc ? numberId : acc;
      }, 0);

      const charData: DataItem = {
        id: String(maxId + 1),
        is_default: false,
        characteristic_id: id,
        percentage: 100,
        deal_id: deal_id,
        value: "",
        currency: null,
        is_deleted: false,
        is_currency: false,
        ...params
      };

      return charData;
    },
    [state, deal_id, id]
  );

  const updateItem = useCallback<UpdateItem>(
    ({ id, is_default, ...rest }) => {
      if (!id) {
        const newCharData = buildNewCharData(rest);
        setState((prevState) => [...prevState, newCharData]);
        return; // Stop updating - add a new one
      }

      // Update existing item
      setState((prevState) => {
        const updatedState = prevState.map((item) => {
          if (item.id !== id) {
            // If setting a new default currency, unset all others
            if (is_default && name === "Currency") {
              return { ...item, is_default: false };
            }
            return item;
          }

          // Update the target item
          return { ...item, ...(is_default !== undefined && { is_default }), ...rest };
        });

        // For currency characteristics, ensure there's always a default if items exist
        if (name === "Currency") {
          const availableItems = updatedState.filter((item) => !item.is_deleted);
          const hasDefault = availableItems.some((item) => item.is_default);

          // If no default exists and we have available items, set the first one as default
          if (!hasDefault && availableItems.length > 0) {
            const firstAvailableId = availableItems[0].id;
            return updatedState.map((item) => ({
              ...item,
              is_default: item.id === firstAvailableId
            }));
          }
        }

        return updatedState;
      });
    },
    [state, setState, buildNewCharData, name]
  );

  const addItem = useCallback(() => {
    const newCharData = buildNewCharData();

    // For currency characteristics, if this is the first item, set it as default
    if (name === "Currency") {
      const hasDefault = state.some((item) => !item.is_deleted && item.is_default);
      if (!hasDefault) {
        newCharData.is_default = true;
      }
    }

    setState((prevState) => [...prevState, newCharData]);
  }, [state, buildNewCharData, name]);

  const removeItem = useCallback(
    (id: DataItem["id"]) => {
      // we need to sent also deleted items to the backend
      return setState((prevState) => {
        const updatedState =
          typeof id === "number"
            ? prevState.map((item) => (item.id === id ? { ...item, is_deleted: true, percentage: 0 } : item))
            : prevState.filter((item) => item.id !== id);

        // For currency characteristics, ensure there's always a default if the removed item was default
        if (name === "Currency") {
          const removedItem = prevState.find((item) => item.id === id);
          const wasDefault = removedItem?.is_default;

          if (wasDefault) {
            const availableItems = updatedState.filter((item) => !item.is_deleted);

            // If we have available items, set the first one as default
            if (availableItems.length > 0) {
              const firstAvailableId = availableItems[0].id;
              return updatedState.map((item) => ({
                ...item,
                is_default: item.id === firstAvailableId
              }));
            }
          }
        }

        return updatedState;
      });
    },
    [state, setState, name]
  );

  const handleSplitPercentage = useCallback(() => {
    if (!state.length) return;
    // state includes also deleted items, so we need to separate them and calculate percentage for available items only
    const { available, deleted } = state.reduce(
      (acc, item) => {
        if (item.is_deleted) {
          acc.deleted.push(item);
        } else {
          acc.available.push(item);
        }
        return acc;
      },
      { available: [], deleted: [] } as { available: DataItem[]; deleted: DataItem[] }
    );

    const splitHundred = available.map((item) => ({
      ...item,
      percentage: Number((100 / available.length).toFixed(decimalPercentage))
    }));

    const delta = 100 - splitHundred.reduce((acc, item) => (acc += Number(item.percentage)), 0);
    if (!delta) {
      return setState([...splitHundred, ...deleted]);
    }

    const addDeltaToLast = splitHundred.map((item, index) =>
      index ? item : { ...item, percentage: item.percentage + delta }
    );

    const sum = addDeltaToLast.reduce((acc, item) => (acc += Number(item.percentage)), 0);

    if (sum !== 100) {
      return console.log("Sub is not equal 100", sum);
    }

    setState([...addDeltaToLast, ...deleted]);
  }, [state, setState]);

  const hasData = state.some((item) => !item.is_deleted && item.value);

  return (
    <Stack
      justifyContent="space-between"
      sx={{
        width: "520px",
        maxHeight: 500,
        padding: "20px 20px 20px 25px",
        boxShadow: "6px -3px 4px rgba(0, 0, 0, 0.25)"
      }}
      gap="20px"
    >
      {viewOnly && !hasData ? (
        <>
          <PopupEmptyState text={`No ${name.toLowerCase()} set for this deal yet.`} />
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <Button variant="outlined" onClick={onCancel}>
              Close
            </Button>
          </Stack>
        </>
      ) : is_multiple ? (
        <MultipleData
          state={state}
          characteristicName={name}
          data_type={data_type}
          updateItem={updateItem}
          onSubmit={() => onSubmit(state)}
          onCancel={onCancel}
          addItem={addItem}
          handleSplitPercentage={handleSplitPercentage}
          removeItem={removeItem}
          viewOnly={viewOnly}
        />
      ) : (
        <SingleData
          data={state[0]}
          data_type={data_type}
          characteristicName={name}
          onUpdate={updateItem}
          onSubmit={() => onSubmit(state)}
          onCancel={onCancel}
          viewOnly={viewOnly}
        />
      )}
    </Stack>
  );
};
